import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  ChevronRight,
  Upload,
  Shield,
  ArrowRight,
  Timer,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useMyDocuments } from '../../hooks/useInviteLinks';

interface KycDocument {
  id: string;
  documentTypeCode: string;
  documentTypeName: string;
  fileName: string;
  originalName: string;
  status: string;
  rejectionReason?: string;
  uploadedAt: string;
  verifiedAt?: string;
}

interface KycSubmissionData {
  id: string;
  kycType: string;
  status: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedByName?: string;
  rejectionReason?: string;
  additionalDocsReason?: string;
  documentsCount: number;
  documents: KycDocument[];
  createdAt: string;
  updatedAt: string;
  // SLA fields
  slaDeadline?: string;
  slaStatus?: 'ON_TIME' | 'WARNING' | 'BREACHED';
  slaRemainingHours?: number;
  estimatedCompletionDate?: string;
  // Two-level approval
  approvalLevel?: number;
  firstLevelApprovedAt?: string;
  firstLevelApprovedByName?: string;
  secondLevelApprovedAt?: string;
  secondLevelApprovedByName?: string;
  requiresTwoLevelApproval?: boolean;
}

interface KycTimelineEvent {
  id: string;
  type: string;
  title: string;
  description?: string;
  createdAt: string;
  createdByName?: string;
  metadata?: Record<string, unknown>;
}

interface KycStatusData {
  origin?: KycSubmissionData;
  destination?: KycSubmissionData;
  canOperate: {
    receive: boolean;
    send: boolean;
  };
  pendingActions: string[];
  timeline?: KycTimelineEvent[];
  estimatedReviewDate?: string;
  resubmissionCount?: number;
  rejectionReason?: string;
}

const statusConfig: Record<string, { 
  label: string; 
  color: string; 
  bgColor: string;
  borderColor: string;
  icon: React.ElementType;
  description: string;
}> = {
  DRAFT: { 
    label: 'Rascunho', 
    color: 'text-surface-400', 
    bgColor: 'bg-surface-500/10',
    borderColor: 'border-surface-500/30',
    icon: Upload,
    description: 'Documentos ainda não enviados',
  },
  SUBMITTED: { 
    label: 'Enviado', 
    color: 'text-blue-400', 
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    icon: Clock,
    description: 'Aguardando início da análise',
  },
  PENDING_REVIEW: { 
    label: 'Em Análise', 
    color: 'text-yellow-400', 
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    icon: Clock,
    description: 'Documentos sendo analisados',
  },
  APPROVED: { 
    label: 'Aprovado', 
    color: 'text-green-400', 
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    icon: CheckCircle2,
    description: 'Verificação concluída com sucesso',
  },
  REJECTED: { 
    label: 'Rejeitado', 
    color: 'text-red-400', 
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    icon: XCircle,
    description: 'Documentos não aprovados',
  },
  ADDITIONAL_DOCS_REQUIRED: { 
    label: 'Docs Adicionais', 
    color: 'text-orange-400', 
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    icon: AlertTriangle,
    description: 'Documentos adicionais necessários',
  },
  IN_REVIEW: { 
    label: 'Em Análise', 
    color: 'text-yellow-400', 
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    icon: Clock,
    description: 'Documentos sendo analisados pela equipe',
  },
};

export function KycStatus() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const virtualAccountId = user?.virtualAccountId;
  const isPendingKyc = user?.status === 'PENDING_KYC';

  // Fetch invite-links documents for PENDING_KYC users
  const { data: inviteDocuments, isLoading: inviteDocsLoading } = useMyDocuments();

  // If user came via invite link (PENDING_KYC), show invite documents view
  if (isPendingKyc) {
    return <InviteKycStatusView documents={inviteDocuments} isLoading={inviteDocsLoading} />;
  }

  const { data: kycStatus, isLoading } = useQuery<KycStatusData>({
    queryKey: ['kyc-status', virtualAccountId],
    queryFn: async () => {
      if (!virtualAccountId) {
        return {
          origin: undefined,
          destination: undefined,
          canOperate: { receive: false, send: false },
          pendingActions: ['Você precisa de uma conta virtual para verificar KYC'],
        };
      }
      const response = await api.get(`/kyc/my-status/${virtualAccountId}`);
      return response.data;
    },
    enabled: true,
  });

  // If user doesn't have a virtual account, show message
  if (!virtualAccountId) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Status da Verificação KYC</h1>
          <p className="text-surface-400 mt-1">
            Acompanhe o status da sua verificação de identidade
          </p>
        </div>
        <div className="glass rounded-xl p-8 text-center border border-yellow-500/30">
          <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-100 mb-2">Conta Virtual Necessária</h3>
          <p className="text-surface-400 mb-4">
            Você precisa ter uma conta virtual vinculada para verificar o status do KYC.
            Entre em contato com o administrador do seu tenant.
          </p>
          <button
            onClick={() => navigate('/banking')}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-surface-800 rounded w-1/3 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 bg-surface-800 rounded-xl animate-pulse" />
          <div className="h-64 bg-surface-800 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  const originStatus = kycStatus?.origin?.status || 'DRAFT';
  const destStatus = kycStatus?.destination?.status || 'DRAFT';
  // Apenas KYC de origem é obrigatório para considerar "completo"
  const allApproved = originStatus === 'APPROVED';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Status da Verificação KYC</h1>
          <p className="text-surface-400 mt-1">
            Acompanhe o status da sua verificação de identidade
          </p>
        </div>
        <Link
          to="/banking/kyc"
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
        >
          <Upload className="w-4 h-4" />
          Enviar Documentos
        </Link>
      </div>

      {/* Status Geral */}
      {allApproved ? (
        <div className="glass rounded-xl p-6 border border-green-500/30 bg-green-500/5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <Shield className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-green-400">KYC Aprovado</h2>
              <p className="text-surface-400 mt-1">
                Sua verificação foi aprovada. Todas as operações da sua conta estão liberadas.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="glass rounded-xl p-4 border border-yellow-500/30 bg-yellow-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-400">Verificação Pendente</h3>
              <p className="text-sm text-surface-400 mt-1">
                Complete sua verificação KYC para liberar todas as funcionalidades da sua conta.
              </p>
              {kycStatus?.pendingActions && kycStatus.pendingActions.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {kycStatus.pendingActions.map((action, i) => (
                    <li key={i} className="text-sm text-surface-400 flex items-center gap-2">
                      <ArrowRight className="w-3 h-3 text-yellow-400" />
                      {action}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cards de Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <KycStatusCard
          title="Minha Verificação KYC"
          description="Verificação obrigatória para operar sua conta"
          submission={kycStatus?.origin}
          canOperate={kycStatus?.canOperate?.receive}
          isPrimary
        />
        <KycStatusCard
          title="KYC de Destino"
          description="Verificação opcional de destino de recursos"
          submission={kycStatus?.destination}
          canOperate={true}
          isOptional
        />
      </div>

      {/* SLA Estimation */}
      {kycStatus?.estimatedReviewDate && !allApproved && (
        <div className="glass rounded-xl p-4 border border-blue-500/30 bg-blue-500/5">
          <div className="flex items-center gap-3">
            <Timer className="w-5 h-5 text-blue-400" />
            <div>
              <h3 className="font-medium text-surface-100">Previsão de Análise</h3>
              <p className="text-sm text-surface-400">
                Estimativa de conclusão: {new Date(kycStatus.estimatedReviewDate).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Info with Resubmission */}
      {kycStatus?.rejectionReason && (originStatus === 'REJECTED' || destStatus === 'REJECTED') && (
        <div className="glass rounded-xl p-4 border border-red-500/30 bg-red-500/5">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-red-400">Motivo da Rejeição</h3>
              <p className="text-sm text-surface-400 mt-1">{kycStatus.rejectionReason}</p>
              {(kycStatus.resubmissionCount === undefined || kycStatus.resubmissionCount < 3) ? (
                <Link
                  to="/banking/kyc"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
                >
                  <Upload className="w-4 h-4" />
                  Reenviar Documentos
                  {kycStatus.resubmissionCount !== undefined && (
                    <span className="text-xs opacity-75">
                      ({3 - kycStatus.resubmissionCount} tentativas restantes)
                    </span>
                  )}
                </Link>
              ) : (
                <p className="text-sm text-surface-500 mt-2">
                  Limite de reenvios atingido. Entre em contato com o suporte.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Operações Permitidas */}
      <div className="glass rounded-xl p-6">
        <h2 className="text-lg font-semibold text-surface-100 mb-4">Operações Permitidas</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <OperationStatus
            label="Receber Pix"
            allowed={kycStatus?.canOperate?.receive || false}
          />
          <OperationStatus
            label="Enviar Pix"
            allowed={kycStatus?.canOperate?.send || false}
          />
          <OperationStatus
            label="Transferências"
            allowed={kycStatus?.canOperate?.send || false}
          />
          <OperationStatus
            label="Saques"
            allowed={kycStatus?.canOperate?.send || false}
          />
        </div>
      </div>
    </div>
  );
}

// Invite KYC Status View for users who registered via invite link
function InviteKycStatusView({ documents, isLoading }: { documents?: Array<{ id: string; documentType: string; fileName: string; originalName: string; mimeType: string; fileSize: number; status: string; rejectionReason?: string; createdAt: string }>; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-surface-800 rounded w-1/3 animate-pulse" />
        <div className="h-32 bg-surface-800 rounded-xl animate-pulse" />
        <div className="h-48 bg-surface-800 rounded-xl animate-pulse" />
      </div>
    );
  }

  const docs = documents || [];
  const allApproved = docs.length > 0 && docs.every(d => d.status === 'APPROVED');
  const anyRejected = docs.some(d => d.status === 'REJECTED');
  const allPending = docs.length > 0 && docs.every(d => d.status === 'PENDING' || d.status === 'UPLOADED');

  const getOverallMessage = () => {
    if (docs.length === 0) return { text: 'Nenhum documento enviado ainda. Envie seus documentos para verificação.', color: 'yellow', icon: AlertTriangle };
    if (allApproved) return { text: 'Todos os documentos foram aprovados. Seu acesso completo será liberado em breve.', color: 'green', icon: CheckCircle2 };
    if (anyRejected) return { text: 'Alguns documentos foram rejeitados. Verifique os motivos e envie novamente.', color: 'red', icon: XCircle };
    if (allPending) return { text: 'Documentos em análise. Aguarde a verificação pelo administrador.', color: 'blue', icon: Clock };
    return { text: 'Documentos em análise. Aguarde a verificação pelo administrador.', color: 'blue', icon: Clock };
  };

  const overall = getOverallMessage();
  const colorMap: Record<string, { bg: string; border: string; text: string }> = {
    green: { bg: 'bg-green-500/5', border: 'border-green-500/30', text: 'text-green-400' },
    red: { bg: 'bg-red-500/5', border: 'border-red-500/30', text: 'text-red-400' },
    yellow: { bg: 'bg-yellow-500/5', border: 'border-yellow-500/30', text: 'text-yellow-400' },
    blue: { bg: 'bg-blue-500/5', border: 'border-blue-500/30', text: 'text-blue-400' },
  };
  const colors = colorMap[overall.color] || colorMap.blue;
  const OverallIcon = overall.icon;

  const docStatusConfig: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
    PENDING: { label: 'Pendente', color: 'text-yellow-400', bg: 'bg-yellow-500/10', icon: Clock },
    UPLOADED: { label: 'Enviado', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Upload },
    APPROVED: { label: 'Aprovado', color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle2 },
    REJECTED: { label: 'Rejeitado', color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-surface-100">Status da Verificação</h1>
        <p className="text-surface-400 mt-1">Acompanhe o status dos seus documentos enviados via convite</p>
      </div>

      {/* Overall Status */}
      <div className={cn('glass rounded-xl p-6 border', colors.border, colors.bg)}>
        <div className="flex items-center gap-4">
          <div className={cn('w-12 h-12 rounded-full flex items-center justify-center', colors.bg)}>
            <OverallIcon className={cn('w-6 h-6', colors.text)} />
          </div>
          <p className={cn('text-lg font-medium', colors.text)}>{overall.text}</p>
        </div>
      </div>

      {/* Documents List */}
      {docs.length > 0 ? (
        <div className="glass rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-surface-100">Documentos Enviados</h2>
            {!allApproved && !allPending && (
              <Link
                to="/banking/kyc"
                className="px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-1.5"
              >
                <Upload className="w-3.5 h-3.5" />
                Enviar Documentos
              </Link>
            )}
          </div>
          <div className="space-y-3">
            {docs.map((doc) => {
              const cfg = docStatusConfig[doc.status] || docStatusConfig.PENDING;
              const DocIcon = cfg.icon;
              return (
                <div key={doc.id} className="flex items-center gap-4 p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                  <FileText className="w-5 h-5 text-surface-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-surface-200 truncate">{doc.documentType}</p>
                    <p className="text-xs text-surface-500 truncate">{doc.originalName}</p>
                    <p className="text-xs text-surface-500 mt-0.5">
                      Enviado em {new Date(doc.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </p>
                  </div>
                  <div className={cn('flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium', cfg.bg, cfg.color)}>
                    <DocIcon className="w-3.5 h-3.5" />
                    {cfg.label}
                  </div>
                </div>
              );
            })}

            {/* Rejection reasons */}
            {docs.filter(d => d.status === 'REJECTED' && d.rejectionReason).map((doc) => (
              <div key={`reject-${doc.id}`} className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                <p className="text-sm text-red-400">
                  <strong>{doc.documentType}:</strong> {doc.rejectionReason}
                </p>
              </div>
            ))}

            {/* Resubmit button when documents are rejected */}
            {anyRejected && (
              <Link
                to="/banking/kyc"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                Reenviar Documentos
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="glass rounded-xl p-8 text-center border border-surface-700">
          <Upload className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-200 mb-2">Nenhum documento enviado</h3>
          <p className="text-surface-400 text-sm mb-4">Envie seus documentos para verificação e ativação da sua conta.</p>
          <Link
            to="/banking/kyc"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
          >
            <Upload className="w-4 h-4" />
            Enviar Documentos
          </Link>
        </div>
      )}
    </div>
  );
}

function KycStatusCard({
  title,
  description,
  submission,
  isPrimary,
  isOptional,
}: {
  title: string;
  description: string;
  submission?: KycSubmissionData;
  canOperate?: boolean;
  isPrimary?: boolean;
  isOptional?: boolean;
}) {
  const status = submission?.status || 'DRAFT';
  const config = statusConfig[status] || statusConfig.DRAFT;
  const Icon = config.icon;

  const needsAction = ['DRAFT', 'REJECTED', 'ADDITIONAL_DOCS_REQUIRED'].includes(status);

  // SLA status colors
  const slaColors = {
    ON_TIME: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30', label: 'No prazo' },
    WARNING: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Em alerta' },
    BREACHED: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', label: 'Atrasado' },
  };

  const slaStyle = submission?.slaStatus ? slaColors[submission.slaStatus] : null;

  return (
    <div className={cn(
      "glass rounded-xl p-6 border",
      config.borderColor,
      isPrimary && "ring-1 ring-primary-500/30"
    )}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
            {title}
            {isOptional && (
              <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-surface-700 text-surface-400">
                Opcional
              </span>
            )}
          </h3>
          <p className="text-sm text-surface-400">{description}</p>
        </div>
        <div className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center",
          config.bgColor
        )}>
          <Icon className={cn("w-5 h-5", config.color)} />
        </div>
      </div>

      {/* Status Badge */}
      <div className={cn(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium",
        config.bgColor,
        config.color
      )}>
        <Icon className="w-4 h-4" />
        {config.label}
      </div>

      <p className="text-sm text-surface-400 mt-3">{config.description}</p>

      {/* SLA Info */}
      {submission?.slaStatus && slaStyle && (status === 'SUBMITTED' || status === 'PENDING_REVIEW') && (
        <div className={cn(
          "mt-4 p-3 rounded-lg border flex items-center justify-between",
          slaStyle.bg, slaStyle.border
        )}>
          <div className="flex items-center gap-2">
            <Timer className={cn("w-4 h-4", slaStyle.text)} />
            <span className={cn("text-sm font-medium", slaStyle.text)}>{slaStyle.label}</span>
          </div>
          {submission.slaRemainingHours !== undefined && submission.slaStatus !== 'BREACHED' && (
            <span className={cn("text-xs", slaStyle.text)}>
              {submission.slaRemainingHours}h restantes
            </span>
          )}
        </div>
      )}

      {/* Expected Completion Date */}
      {submission?.slaDeadline && (status === 'SUBMITTED' || status === 'PENDING_REVIEW') && (
        <div className="mt-3 flex items-center gap-2 text-sm text-surface-400">
          <Calendar className="w-4 h-4" />
          <span>Previsão: {new Date(submission.slaDeadline).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}</span>
        </div>
      )}

      {/* Two-Level Approval Progress */}
      {submission?.requiresTwoLevelApproval && (status === 'SUBMITTED' || status === 'PENDING_REVIEW' || status === 'APPROVED') && (
        <div className="mt-4 p-3 bg-surface-800 rounded-lg">
          <h4 className="text-xs font-medium text-surface-400 mb-2">Aprovação em Dois Níveis</h4>
          <div className="flex items-center gap-2">
            <div className={cn(
              "flex-1 h-2 rounded-full",
              submission.approvalLevel && submission.approvalLevel >= 1 ? "bg-green-500" : "bg-surface-600"
            )} />
            <div className={cn(
              "flex-1 h-2 rounded-full",
              submission.approvalLevel === 2 ? "bg-green-500" : "bg-surface-600"
            )} />
          </div>
          <div className="flex justify-between mt-1 text-xs text-surface-500">
            <span className={submission.approvalLevel && submission.approvalLevel >= 1 ? "text-green-400" : ""}>
              1º Nível {submission.firstLevelApprovedByName && `(${submission.firstLevelApprovedByName})`}
            </span>
            <span className={submission.approvalLevel === 2 ? "text-green-400" : ""}>
              2º Nível {submission.secondLevelApprovedByName && `(${submission.secondLevelApprovedByName})`}
            </span>
          </div>
        </div>
      )}

      {/* Motivo de Rejeição */}
      {status === 'REJECTED' && submission?.rejectionReason && (
        <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
          <p className="text-sm text-red-400">
            <strong>Motivo:</strong> {submission.rejectionReason}
          </p>
        </div>
      )}

      {/* Documentos Adicionais */}
      {status === 'ADDITIONAL_DOCS_REQUIRED' && submission?.additionalDocsReason && (
        <div className="mt-4 p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
          <p className="text-sm text-orange-400">
            {submission.additionalDocsReason}
          </p>
        </div>
      )}

      {/* Documentos */}
      {submission?.documents && submission.documents.length > 0 && (
        <div className="mt-4 pt-4 border-t border-surface-700">
          <h4 className="text-sm font-medium text-surface-300 mb-2">
            Documentos ({submission.documentsCount})
          </h4>
          <div className="space-y-2">
            {submission.documents.slice(0, 3).map((doc) => (
              <div key={doc.id} className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-surface-500" />
                <span className="text-surface-400 flex-1 truncate">{doc.documentTypeName}</span>
                {doc.status === 'VERIFIED' && (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                )}
                {doc.status === 'REJECTED' && (
                  <XCircle className="w-4 h-4 text-red-400" />
                )}
                {doc.status === 'UPLOADED' && (
                  <Clock className="w-4 h-4 text-yellow-400" />
                )}
              </div>
            ))}
            {submission.documents.length > 3 && (
              <p className="text-xs text-surface-500">
                +{submission.documents.length - 3} documentos
              </p>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      {submission && (
        <div className="mt-4 pt-4 border-t border-surface-700">
          <div className="space-y-2 text-xs text-surface-500">
            {submission.submittedAt && (
              <p>Enviado em {new Date(submission.submittedAt).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}</p>
            )}
            {submission.reviewedAt && (
              <p>Analisado em {new Date(submission.reviewedAt).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })} {submission.reviewedByName && `por ${submission.reviewedByName}`}</p>
            )}
          </div>
        </div>
      )}

      {/* Ação */}
      {needsAction && (
        <Link
          to="/banking/kyc"
          className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          {status === 'DRAFT' ? 'Enviar Documentos' : 'Corrigir Documentos'}
          <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

function OperationStatus({ label, allowed }: { label: string; allowed: boolean }) {
  return (
    <div className={cn(
      "p-3 rounded-lg border",
      allowed
        ? "bg-green-500/10 border-green-500/30"
        : "bg-surface-800 border-surface-700"
    )}>
      <div className="flex items-center gap-2">
        {allowed ? (
          <CheckCircle2 className="w-4 h-4 text-green-400" />
        ) : (
          <XCircle className="w-4 h-4 text-surface-500" />
        )}
        <span className={cn(
          "text-sm font-medium",
          allowed ? "text-green-400" : "text-surface-500"
        )}>
          {label}
        </span>
      </div>
    </div>
  );
}

export default KycStatus;
